import moment from "moment";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { getActivePrestationBrandConfig } from "../../config/environment.config";
import { ClientSessionInfo } from "../../Repositories/ClientSessionRepository";
import { clock, dayLabel, euros } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { courseDatesLabel, courseDatesSubline, courseSessions } from "../CoursesV2/courseRows";
import "moment/locale/fr";

/**
 * Lecture d'un cours de l'API client (`dashboard_courses`, format
 * `_punctualCourseToDictForClientDashboard`) pour l'espace client repensé :
 * nom du professeur, dates, montant payé, format. Aucune règle nouvelle,
 * seulement de la mise en forme. Le client ne voit jamais la rémunération du
 * prof ni les frais : seulement le prix total TTC et ce qu'il reste à payer
 * après l'avance immédiate.
 */

const toNumber = (value: unknown): number => {
    const n = typeof value === "number" ? value : parseFloat(String(value ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
};

/** « Jean Dupont », à défaut « Votre professeur ». */
export const courseTeachrName = (c: any): string => {
    const name = [c?.teachrFirstName, c?.teachrLastName].filter(Boolean).map((s: string) => capitalizeFirstLetter(s)).join(" ");
    return name || `Votre ${vocab.teachr()}`;
};

export const courseTeachrInitials = (c: any): string =>
    `${(c?.teachrFirstName ?? "").charAt(0)}${(c?.teachrLastName ?? "").charAt(0)}`.toUpperCase() || "?";

/** Élève du cours quand il a été pris pour un enfant, sinon null. */
export const courseChildName = (c: any): string | null => (c?.forChildFirstName ? capitalizeFirstLetter(c.forChildFirstName) : null);

/** Prix total TTC du cours, tel qu'il est enregistré (prestation + frais). */
export const courseTotalTtc = (c: any): number => toNumber(c?.priceCf?.priceWithFees ?? c?.amount?.priceWithFees);

/** Part avancée par l'URSSAF : la moitié du prix total, quand l'avance immédiate est active. */
export const courseAdvance = (c: any, aiciActive: boolean): number => (aiciActive ? courseTotalTtc(c) / 2 : 0);

/** Ce que paie le client : le prix total, moins l'avance immédiate quand elle est active. */
export const coursePaid = (c: any, aiciActive: boolean): number => courseTotalTtc(c) - courseAdvance(c, aiciActive);

/** « Anglais, plusieurs cours » / « Soutien, un seul cours » (à distance précisé). */
export const courseSubjectLine = (c: any): string => {
    const subject = capitalizeFirstLetter(c?.subjectAsked ?? "") || vocab.subjectLabel();
    const format = c?.isPunctualMonth ? vocab.multipleCoursesTitle() : vocab.singleCourseTitle();
    return `${subject}, ${format.charAt(0).toLowerCase()}${format.slice(1)}${c?.isRemote ? ", à distance" : ""}`;
};

/** Format seul : « Plusieurs cours » / « Cours ponctuel ». */
export const courseFormatLabel = (c: any): string =>
    c?.isPunctualMonth ? `Plusieurs ${getActivePrestationBrandConfig("s")}` : capitalizeFirstLetter(getActivePrestationBrandConfig(" ponctuelle", "", "", " ponctuel"));

/** Mêmes libellés de dates que la liste du prof. */
export { courseDatesLabel, courseDatesSubline, courseSessions };

/** Nombre d'heures du cours (la durée de l'API est en demi-heures). */
export const courseHours = (c: any): number => toNumber(c?.duration) / 2;

/** « 40,00 € » puis « avance immédiate déduite » en rappel. */
export const paidLabel = (aiciActive: boolean): string => (aiciActive ? "Vous payez" : "Prix total");

// ─── Séances de l'API client (accueil : prochains cours) ─────────────────────

/** « Mardi 4 novembre, 17h00 à 18h00 ». */
export const sessionWhen = (s: ClientSessionInfo): string => {
    if (!s.date) return "Date à confirmer";
    const start = moment(s.date);
    const end = s.endDate ? moment(s.endDate) : start.clone().add(s.durationMinutes ?? 0, "minutes");
    return `${capitalizeFirstLetter(dayLabel(start, !start.isSame(moment(), "year")))}, ${clock(start)} à ${clock(end)}`;
};

/** « Anglais avec Jean Dupont, pour Léa ». */
export const sessionWho = (s: ClientSessionInfo): string => {
    const teachr = s.teachr ? [s.teachr.firstName, s.teachr.lastName].filter(Boolean).map((v) => capitalizeFirstLetter(v)).join(" ") : "";
    const subject = s.course?.subject ? capitalizeFirstLetter(s.course.subject) : capitalizeFirstLetter(vocab.course());
    const child = s.childFirstName ? `, pour ${capitalizeFirstLetter(s.childFirstName)}` : "";
    return `${subject}${teachr ? ` avec ${teachr}` : ""}${child}`;
};

/** « 60,00 € TTC » ou null quand le montant n'est pas connu. */
export const sessionAmountLabel = (s: ClientSessionInfo): string | null => (s.amount != null ? `${euros(s.amount)} TTC` : null);

/** Jours avant la séance : 0 aujourd'hui, 1 demain… */
export const daysUntil = (s: ClientSessionInfo): number | null => (s.date ? moment(s.date).startOf("day").diff(moment().startOf("day"), "days") : null);

/** « Aujourd'hui », « Demain », « Dans 5 jours », null quand la séance est passée. */
export const sessionCountdown = (s: ClientSessionInfo): string | null => {
    const d = daysUntil(s);
    if (d === null || d < 0) return null;
    if (d === 0) return "Aujourd'hui";
    if (d === 1) return "Demain";
    return `Dans ${d} jours`;
};
